import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import DOMPurify from "dompurify";
import { NodeProps, Node, useReactFlow } from "@xyflow/react";
import { Send, Plus, Loader2, ChevronDown, Sparkles, Eye, X, AlertTriangle, Mail } from "lucide-react";
import gmailService from "../../services/gmail.service";
import syntaxHighlight from "../../utils/jsonViewer";
import WorkflowBaseNode from "../../common/shared/WorkflowBaseNode";
import WorkflowNodeModal from "../../common/shared/WorkflowNodeModal";

type CredentialItem = {
  _id: string;
  user_info: {
    user_email: string;
    user_name: string;
  };
};

export const GmailSendNode: React.FC<NodeProps<Node<any>>> = ({ id, data, selected }) => {
  const { setNodes, getNodes } = useReactFlow();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [hasLoadedCredentials, setHasLoadedCredentials] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(data?.saved_html || null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState<string | null>(null);

  const authPopupRef = useRef<Window | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [selectedCredentialId, setSelectedCredentialId] = useState<string>(data?.gmail_credential_id || data?.inputs?.gmail_credential_id || "");
  
  const [to, setTo] = useState<string>(() => {
    const initialTo = data?.to || data?.inputs?.to;
    if (Array.isArray(initialTo)) return initialTo.join(", ");
    return initialTo || "";
  });
  
  const [subject, setSubject] = useState<string>(data?.subject || data?.inputs?.subject || "");
  // Message body is a list of variables / text chips that get concatenated on save.
  const [bodyParts, setBodyParts] = useState<string[]>(() => {
    const raw: string = data?.body || data?.inputs?.body || "";
    if (!raw) return [];
    const tokens: string[] = [];
    const re = /\{\{[^}]+\}\}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(raw)) !== null) {
      if (match.index > lastIndex) {
        const txt = raw.slice(lastIndex, match.index).trim();
        if (txt) tokens.push(txt);
      }
      tokens.push(match[0]);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < raw.length) {
      const tail = raw.slice(lastIndex).trim();
      if (tail) tokens.push(tail);
    }
    return tokens.length > 0 ? tokens : [raw];
  });
  const body = useMemo(() => bodyParts.join(" "), [bodyParts]);
  const addBodyChip = (chip: string) => setBodyParts((prev) => [...prev, chip]);
  const removeBodyChip = (idx: number) =>
    setBodyParts((prev) => prev.filter((_, i) => i !== idx));
  const [aiPrompt, setAiPrompt] = useState<string>(data?.ai_prompt || data?.inputs?.ai_prompt || "");
  // Attachments: accepts a single URL, a comma/newline-separated list of URLs,
  // or a workflow variable like {{DocumentStoreNode.outputs.file_ids}} (drag-dropped from sidebar)
  const [attachments, setAttachments] = useState<string>(() => {
    const raw = data?.attachments ?? data?.inputs?.attachments;
    if (Array.isArray(raw)) return raw.join("\n");
    if (typeof raw === "string") return raw;
    return "";
  });
  
  const [credentials, setCredentials] = useState<CredentialItem[]>([]);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      const allowed = ["http://localhost:3000", "http://localhost:5173", "https://nightly-dms.skill-mine.com", "https://demo-dms.skill-mine.com"];
      if (!allowed.includes(event.origin)) return;
      
      if (
        event.data?.type === "GMAIL_AUTH_SUCCESS" || 
        event.data?.status === "success" || 
        (typeof event.data === 'string' && event.data.includes('success'))
      ) {
        setConnecting(false);
        loadCredentials();
        if (authPopupRef.current && !authPopupRef.current.closed) authPopupRef.current.close();
      }
    };
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, []);

  useEffect(() => {
    if (!connecting) return;
    const pollInterval = setInterval(() => {
      const popup = authPopupRef.current;
      if (popup && popup.closed) {
        authPopupRef.current = null;
        setConnecting(false);
        loadCredentials();
        clearInterval(pollInterval);
      }
    }, 1000);
    return () => clearInterval(pollInterval);
  }, [connecting]);

  const openAuthPopup = async () => {
    try {
      setConnecting(true);
      setErrorMsg(null);
      const templateId = data?.template_id || localStorage.getItem("template_id");
      const authUrl = gmailService.getGmailLoginUrl(templateId || undefined);
      const width = 500, height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      const popup = window.open(authUrl, "GmailAuthPopup", `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
      if (!popup) throw new Error("Popup blocked! Please allow popups for this site.");
      authPopupRef.current = popup;
      popup.focus();
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to open login popup");
      setConnecting(false);
    }
  };

  const loadCredentials = async () => {
    if (loadingCredentials) return;
    const templateId = data?.template_id || localStorage.getItem("template_id");
    try {
      setLoadingCredentials(true);
      const response = await gmailService.listCredentials(templateId);
      if (response && response.success) {
        setCredentials(response.data || []);
      }
      setHasLoadedCredentials(true);
    } catch (err: any) {
      setErrorMsg(err?.message || "Failed to load credentials");
    } finally {
      setLoadingCredentials(false);
    }
  };

  useEffect(() => {
    if (isModalOpen && !hasLoadedCredentials && !loadingCredentials) {
      loadCredentials();
    }
  }, [isModalOpen]);

  const selectedCredentialInfo = useMemo(() => {
    const cred = credentials.find((c) => c._id === selectedCredentialId);
    return cred?.user_info || data?.selected_credential_info || null;
  }, [credentials, selectedCredentialId, data?.selected_credential_info]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id !== id) return n;
        const toArray = to.split(",").map(s => s.trim()).filter(Boolean);
        const nodeData = n.data as Record<string, any>;
        const cleanedData = { ...nodeData };
        delete cleanedData.inputs;
        delete cleanedData.selected_credential_info;
        
        // Normalise attachments: if it's a single workflow expression like
        // {{DocStoreNode.outputs.file_ids}}, leave as a string so the backend
        // resolver evaluates it. Otherwise split on newlines/commas into URLs.
        const trimmedAtt = attachments.trim();
        let attachmentsVal: any = "";
        if (trimmedAtt) {
          const looksLikeSingleExpression = /^\{\{[^}]+\}\}$/.test(trimmedAtt);
          if (looksLikeSingleExpression) {
            attachmentsVal = trimmedAtt;
          } else {
            const parts = trimmedAtt.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
            attachmentsVal = parts.length <= 1 ? (parts[0] ?? "") : parts;
          }
        }

        return {
          ...n,
          data: {
            ...cleanedData,
            gmail_credential_id: selectedCredentialId,
            to: toArray,
            subject,
            body,
            ai_prompt: aiPrompt,
            saved_html: previewHtml,
            attachments: attachmentsVal,
          },
        };
      })
    );
  }, [selectedCredentialId, to, subject, body, aiPrompt, previewHtml, attachments, id, setNodes]);

  const generateAiPreview = async () => {
    try {
      setGeneratingPreview(true);
      setErrorMsg(null);
      
      const allNodes = getNodes();
      let resolvedBody = body;
      const regex = /\{\{([^}]+)\}\}/g;
      let match;
      const processedVars = new Set<string>();

      while ((match = regex.exec(body)) !== null) {
        const fullVar = match[1].trim();
        if (processedVars.has(fullVar)) continue;
        processedVars.add(fullVar);

        const parts = fullVar.split('.');
        let currentData: any = undefined;

        // 1. Try resolving with Node Name as first part
        const nodeNameOrKey = parts[0];
        const sourceNode = allNodes.find(n => 
          n.id.toLowerCase() === nodeNameOrKey.toLowerCase() || 
          (n.data as any)?.label?.toLowerCase() === nodeNameOrKey.toLowerCase() ||
          (n.data as any)?.name?.toLowerCase() === nodeNameOrKey.toLowerCase()
        );

        if (sourceNode) {
          currentData = (sourceNode.data as any)?.outputs || (sourceNode.data as any)?.results;
          for (let i = 1; i < parts.length; i++) {
            if (currentData && typeof currentData === 'object') {
              const part = parts[i];
              if (part === 'outputs' && i === 1) continue;
              currentData = currentData[part];
            }
          }
        }

        // 2. Fuzzy Global Resolution: Search for parts[0] in all node outputs
        if (currentData === undefined) {
          for (const node of allNodes) {
            const outputs = (node.data as any)?.outputs || (node.data as any)?.results;
            if (outputs && typeof outputs === 'object' && nodeNameOrKey in outputs) {
              currentData = outputs;
              for (let i = 0; i < parts.length; i++) {
                if (currentData && typeof currentData === 'object') {
                  currentData = currentData[parts[i]];
                }
              }
              if (currentData !== undefined) break;
            }
          }
        }

        // 3. Fallback: Check direct outputs/inputs of current node
        if (currentData === undefined) {
          const selfData = data?.outputs || data?.inputs;
          if (selfData && typeof selfData === 'object' && nodeNameOrKey in selfData) {
            currentData = selfData;
            for (let i = 0; i < parts.length; i++) {
              if (currentData && typeof currentData === 'object') {
                currentData = currentData[parts[i]];
              }
            }
          }
        }

        if (currentData !== undefined) {
          const replacement = typeof currentData === 'object' ? JSON.stringify(currentData, null, 2) : String(currentData);
          resolvedBody = resolvedBody.replace(new RegExp(match[0].replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replacement);
        }
      }

      if (!resolvedBody.trim()) {
        setPreviewHtml(null);
        return;
      }

      let sampleData: any = resolvedBody;
      const trimmed = resolvedBody.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try { sampleData = JSON.parse(trimmed); } catch (e) {}
      }

      const contentJson = typeof sampleData === 'object' ? JSON.stringify(sampleData, null, 2) : String(sampleData);
      const response = await gmailService.generateTemplate(aiPrompt || "", [contentJson]);
      
      if (response && response.data && response.data.html) {
        setPreviewHtml(
          DOMPurify.sanitize(response.data.html, {
            ADD_TAGS: ["style"],
            ADD_ATTR: ["target", "rel"],
          })
        );
      } else {
        setErrorMsg("Preview content could not be generated.");
      }
    } catch (err: any) {
      setErrorMsg("Failed to generate: " + (err.response?.data?.message || err.message));
    } finally {
      setGeneratingPreview(false);
    }
  };

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!selectedCredentialId) errors.push("Gmail account is required");
    if (!to.trim()) errors.push("Recipient email (To) is required");
    if (!subject.trim()) errors.push("Subject is required");
    if (!body.trim()) errors.push("Message body is required");
    return errors;
  }, [selectedCredentialId, to, subject, body]);

  const isConfigured = validationErrors.length === 0;

  const renderOutputsSidebar = useCallback(() => (
    <div className="w-80 bg-slate-50 overflow-y-auto p-4 border-l [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
          <div className="w-2 h-2 bg-red-500 rounded-full" />
          Node Outputs
        </h3>
        <p className="text-xs text-slate-500">Execution result</p>
      </div>
      {data.outputs ? (
        <div className="bg-white border rounded-lg p-3">
          <pre className="text-xs font-mono break-all whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: syntaxHighlight(data.outputs) }} />
        </div>
      ) : (
        <div className="p-6 bg-white border border-dashed rounded-lg text-center">
          <Send className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-xs text-slate-500 font-medium">No output yet</p>
        </div>
      )}
    </div>
  ), [data.outputs]);

  const handleDrop = (e: React.DragEvent, targetField: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(null);
    const variable = e.dataTransfer.getData("text/plain");
    if (!variable) return;
    if (targetField === "to") setTo(prev => prev + (prev ? ", " : "") + variable);
    else if (targetField === "subject") setSubject(prev => prev + (prev ? " " : "") + variable);
    else if (targetField === "body") {
      addBodyChip(variable);
      setPreviewHtml(null);
      setIsPreviewModalOpen(false);
    }
    else if (targetField === "ai_prompt") {
      setAiPrompt(prev => prev + (prev ? " " : "") + variable);
      setPreviewHtml(null);
      setIsPreviewModalOpen(false);
    }
    else if (targetField === "attachments") {
      setAttachments(prev => prev ? `${prev}\n${variable}` : variable);
    }
  };

  return (
    <WorkflowBaseNode
      id={id}
      data={data}
      selected={selected}
      title="Gmail Send"
      icon={<Send className="w-4 h-4 text-red-600" />}
      nodeDocsType="GMAIL_SEND"
      themeColor="red"
      configureLabel="Configure Email"
      isConfigured={isConfigured}
      validationErrors={validationErrors}
      onConfigure={() => setIsModalOpen(true)}
      hasSourceHandle={true}
      hasErrorHandle={false}
    >
      {selectedCredentialInfo && (
        <div className="mt-2 pt-2 border-t text-[10px] text-slate-500">
          <p className="truncate">From: <span className="font-medium text-slate-700">{selectedCredentialInfo.user_email}</span></p>
          {to && <p className="truncate">To: <span className="font-medium text-slate-700">{to}</span></p>}
        </div>
      )}

      <WorkflowNodeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Gmail Configuration"
        icon={<Mail className="w-5 h-5 text-red-600" />}
        themeColor="red"
        nodeId={id}
        nodeData={data}
        nodeIcon={<Send className="w-6 h-6 text-slate-400" />}
        validationStatus={{ isValid: isConfigured, errors: validationErrors }}
        renderOutputsSidebar={renderOutputsSidebar}
      >
        <div className="py-2">
          <div className="space-y-5">

            {/* Connection Section — full width card */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <div className="p-1.5 bg-red-100 rounded text-red-600">
                  <Mail className="w-4 h-4" />
                </div>
                Connection Settings
              </h3>
              <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Sender Account <span className="text-red-500">*</span></label>
                <div className="relative" ref={dropdownRef}>
                  <div onClick={() => { if (!isDropdownOpen) { loadCredentials(); } setIsDropdownOpen(!isDropdownOpen); }} 
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer flex items-center justify-between hover:border-red-400 transition-colors">
                    <span className={selectedCredentialId ? "text-gray-900 font-medium" : "text-gray-400"}>
                      {selectedCredentialInfo ? `${selectedCredentialInfo.user_name} (${selectedCredentialInfo.user_email})` : "Select Gmail Account..."}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </div>
                  {isDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto py-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
                      {credentials.map((cred) => (
                        <div key={cred._id} onClick={() => { setSelectedCredentialId(cred._id); setIsDropdownOpen(false); }}
                          className={`px-4 py-2 text-sm cursor-pointer hover:bg-red-50 flex flex-col ${selectedCredentialId === cred._id ? "bg-red-50 border-l-2 border-red-500" : "text-gray-700"}`}>
                          <span className="font-medium text-gray-900">{cred.user_info.user_name}</span>
                          <span className="text-xs text-gray-500">{cred.user_info.user_email}</span>
                        </div>
                      ))}
                      <button onClick={(e) => { e.stopPropagation(); openAuthPopup(); setIsDropdownOpen(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-gray-100 mt-1 font-medium">
                        <Plus className="w-4 h-4" /> Add New Account
                      </button>
                    </div>
                  )}
                </div>
                {errorMsg && <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {errorMsg}</p>}
              </div>
            </div>
          </div>

          {/* Email Details + AI Formatting — 2-column grid */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <div className="p-1.5 bg-red-100 rounded text-red-600">
                  <Send className="w-4 h-4" />
                </div>
                Email Details
              </h3>

              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Recipient (To) <span className="text-red-500">*</span></label>
                  <input type="text" value={to} onChange={(e) => setTo(e.target.value)} onDrop={(e) => handleDrop(e, "to")} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; }}
                      className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none" placeholder="e.g. user@gmail.com" />
                </div>
                
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700">Subject Line <span className="text-red-500">*</span></label>
                  <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} onDrop={(e) => handleDrop(e, "subject")} onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; }}
                      className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none" placeholder="Enter email subject" />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
                    <span>Message Content / Variables <span className="text-red-500">*</span></span>
                    {bodyParts.length > 0 && (
                      <button
                        type="button"
                        onClick={() => { setBodyParts([]); setPreviewHtml(null); setIsPreviewModalOpen(false); }}
                        className="text-[10px] text-gray-400 hover:text-red-500"
                      >
                        Clear all ({bodyParts.length})
                      </button>
                    )}
                  </label>
                  <div
                    onDrop={(e) => handleDrop(e, "body")}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; setIsDraggingOver("body"); }}
                    onDragLeave={() => setIsDraggingOver(null)}
                    className={`min-h-[80px] border-2 border-dashed rounded-lg p-2 flex flex-wrap items-start content-start gap-1.5 transition-colors ${
                      bodyParts.length > 0
                        ? "border-red-200 bg-white"
                        : isDraggingOver === "body"
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300 bg-gray-50 hover:border-red-300"
                    }`}
                  >
                    {bodyParts.length > 0 ? (
                      <>
                        {bodyParts.map((part, idx) => {
                          const isVar = /^\{\{.+\}\}$/.test(part);
                          return (
                            <span
                              key={`${part}-${idx}`}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono ${
                                isVar
                                  ? "bg-red-100 text-red-800 border border-red-200"
                                  : "bg-gray-100 text-gray-800 border border-gray-200"
                              }`}
                            >
                              <span className="truncate max-w-[280px]" title={part}>{part}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeBodyChip(idx);
                                  setPreviewHtml(null);
                                  setIsPreviewModalOpen(false);
                                }}
                                className="text-red-500 hover:text-red-700 flex-shrink-0"
                                title="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          );
                        })}
                        <span className="text-[11px] text-gray-400 italic px-1 py-1">Drag more variables here…</span>
                      </>
                    ) : (
                      <div className="w-full text-center text-xs text-gray-400 italic py-4">
                        Drag one or more variables from the left sidebar here
                      </div>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-500">
                    Variables resolve at runtime — chip order is the order they appear in the email body. Drag multiple if you need composite content.
                  </p>
                </div>

                {/* Attachments (optional) — accepts URL(s) or a workflow variable */}
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-700 flex items-center justify-between">
                    <span>
                      Attachments <span className="text-gray-400 font-normal">(optional)</span>
                    </span>
                    {attachments && (
                      <button
                        type="button"
                        onClick={() => setAttachments("")}
                        className="text-[10px] text-gray-400 hover:text-red-500"
                      >
                        Clear
                      </button>
                    )}
                  </label>
                  <textarea
                    value={attachments}
                    onChange={(e) => setAttachments(e.target.value)}
                    onDrop={(e) => handleDrop(e, "attachments")}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; setIsDraggingOver("attachments"); }}
                    onDragLeave={() => setIsDraggingOver(null)}
                    rows={3}
                    placeholder={'Paste a file URL, list multiple URLs (one per line), OR drag a variable like {{DocumentStoreNode.outputs.file_ids}}'}
                    className={`w-full p-2.5 text-xs font-mono border-2 rounded-lg outline-none transition-colors resize-none ${
                      isDraggingOver === "attachments"
                        ? "border-red-300 bg-red-50"
                        : attachments
                          ? "border-green-300 bg-green-50 text-green-800"
                          : "border-dashed border-gray-300 bg-gray-50 focus:border-red-500 focus:bg-white"
                    }`}
                  />
                  <p className="text-[10px] text-gray-500">
                    Files referenced here will be downloaded and attached to the outgoing email (max 25 MB each).
                  </p>
                </div>
              </div>
            </div>

              {/* AI Formatting — second column of the grid */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <div className="p-1.5 bg-indigo-100 rounded text-indigo-600">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  AI Formatting
                </h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-xs font-medium text-gray-700">Formatting Instructions</label>
                    <div className="flex gap-2">
                      <button onClick={generateAiPreview} disabled={generatingPreview || !body.trim() || !aiPrompt.trim()}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-medium rounded-md transition-colors disabled:opacity-50">
                        {generatingPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        Generate Preview
                      </button>
                      {previewHtml && (
                        <button onClick={() => setIsPreviewModalOpen(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-[11px] font-medium rounded-md hover:bg-gray-50 transition-colors">
                          <Eye className="w-3.5 h-3.5" /> Preview
                        </button>
                      )}
                    </div>
                  </div>

                  <textarea
                    value={aiPrompt}
                    onChange={(e) => {
                      setAiPrompt(e.target.value);
                      setPreviewHtml(null);
                      setIsPreviewModalOpen(false);
                    }}
                    onDrop={(e) => handleDrop(e, "ai_prompt")}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = "copy"; }}
                    rows={6}
                    placeholder="e.g., 'Format this data as a professional HTML table'..."
                    className="w-full p-3 text-sm outline-none resize-none bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </WorkflowNodeModal>

      {/* Simple Email Preview Modal */}
      {isPreviewModalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10001] bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
              <div className="flex items-center gap-3">
                <div className="p-1.5 bg-red-50 rounded-lg text-red-600">
                  <Mail className="w-5 h-5" />
                </div>
                <h2 className="text-base font-semibold text-gray-900">Gmail Preview</h2>
              </div>
              <button 
                onClick={() => setIsPreviewModalOpen(false)} 
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 bg-slate-50 p-4 md:p-8 overflow-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-300">
              <div className="bg-white w-full max-w-4xl mx-auto shadow-sm border border-slate-200 rounded-lg p-8 min-h-full">
                  {previewHtml ? (
                  <div className="email-preview-content" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-gray-400 py-20">
                    <Mail className="w-12 h-12 mb-2 opacity-20" />
                    <p className="text-sm">No preview content generated.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer with Close Button */}
            <div className="px-6 py-4 border-t bg-white flex justify-end">
              <button 
                onClick={() => setIsPreviewModalOpen(false)}
                className="px-8 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </WorkflowBaseNode>
  );
};

export default GmailSendNode;
